<template>
  <div v-if="stores" class="ExperienceStoreResults flex flex-col gap-12">
    <!-- * * * * stores within the radius -->
    <template v-if="storesInRadius.length">
      <div class="font-open font-semibold text-14 leading-20">
        {{ $t(storesInRadius.length === 1 ? 'pdp.experience.results.countOne' : 'pdp.experience.results.count', { count: storesInRadius.length, radius: DISPLAY_RADIUS_KM }) }}
      </div>
      <ol class="flex flex-col gap-8">
        <li v-for="store in visibleStores" :key="store.accountid">
          <ExperienceStoreCard :store="store" />
        </li>
      </ol>
      <button
        v-if="hiddenCount > 0"
        type="button"
        class="font-open font-semibold text-14 leading-20 underline w-fit min-h-44 py-4 text-left"
        @click="showAll = true"
      >
        {{ $t(hiddenCount === 1 ? 'pdp.experience.results.showMoreOne' : 'pdp.experience.results.showMore', { count: hiddenCount }) }}
      </button>
    </template>

    <!-- * * * * only a store further away -->
    <template v-else-if="stores.length">
      <div class="font-open font-semibold text-14 leading-20">{{ $t('pdp.experience.results.noneWithin', { radius: DISPLAY_RADIUS_KM }) }}</div>
      <ExperienceStoreCard :store="stores[0]!" />
    </template>

    <!-- * * * * no store at all -->
    <template v-else>
      <div class="font-open text-12 leading-16">{{ $t('pdp.experience.results.fallback') }}</div>
      <NuxtLink
        v-if="$sbLinkCheck(globalContent.config?.findRetailerPage)"
        :to="$sbLinkFix(globalContent.config?.findRetailerPage)"
        :target="$sbLinkTarget(globalContent.config?.findRetailerPage)"
        class="btn w-fit"
      >
        {{ $t('findRetailer.findbutton') }}
      </NuxtLink>
    </template>
  </div>
</template>

<script setup lang="ts">
import ExperienceStoreCard from '~/components/product-page/ExperienceStoreCard.vue';
import useDisplaySamples from '~/composables/useDisplaySamples';
import useRetailerLocations from '~/composables/useRetailerLocations';
import { useGlobalContentStore } from '~/store/globalContent';
import { useLocationStore } from '~/store/location';
import { DISPLAY_RADIUS_KM, getDisplayStores } from '~/util/displayStores';

const VISIBLE_COUNT = 3;

const props = defineProps<{
  partNo: string,
}>();

const globalContent = useGlobalContentStore();
const locationStore = useLocationStore();
const { retailers, load: loadRetailers } = useRetailerLocations();
const { samples, request } = useDisplaySamples();

onMounted(() => loadRetailers());

watch(() => props.partNo, partNo => request(partNo), { immediate: true });

const showAll = ref(false);

watch(() => locationStore.location, () => {
  showAll.value = false;
});

// null while the retailers or the display samples are loading, a failed request counts as no stores
const stores = computed(() => {
  const location = locationStore.location;
  const ids = samples.value[props.partNo];
  if (!location || !retailers.value || ids === undefined) {
    return null;
  }
  return getDisplayStores(ids ?? [], retailers.value, location);
});

const storesInRadius = computed(() => (stores.value ?? []).filter(s => s.distanceKm <= DISPLAY_RADIUS_KM));
const visibleStores = computed(() => showAll.value ? storesInRadius.value : storesInRadius.value.slice(0, VISIBLE_COUNT));
const hiddenCount = computed(() => storesInRadius.value.length - visibleStores.value.length);
</script>
